import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IApplicationState,
  LoginCredentials,
  UserProfile,
} from "../../../types/application";
import { AppDispatch } from "redux/store";
import { api } from "api/axiosInterceptor";
import { apiUrl } from "api/apiUrl";
import { convertToCamelCase } from "utils/utils";
import {
  ContactForm,
  ContactSalesForm,
} from "components/dashboard/settings/support/types";

const initialState: IApplicationState = {
  isLoading: false,
  isLoadingModal: false,
  successLogin: false,
  showToast: false,
  showError: false,
  userProfile: {
    userDetails: [],
    hasAccess: false,
  },
  toastMessage: "",
  type: "",
  toastDuration: 2000,
  pendingRequests: 0,
  requires2FA: false,
  userEmail: "",
};

const slice = createSlice({
  name: "application",
  initialState,
  reducers: {
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoadingModal = action.payload;
    },
    loginsuccess(state, action: PayloadAction<boolean>) {
      state.successLogin = action.payload;
    },
    userProfileData(state, action: PayloadAction<UserProfile>) {
      const { remainingProjectQuota } = action.payload?.userDetails[0];

      const hasAccess = remainingProjectQuota > 0;
      state.userProfile = { ...action.payload, hasAccess };
    },
    updateProfileData(state, action) {
      if (state.userProfile.userDetails.length > 0) {
        state.userProfile.userDetails[0] = {
          ...state.userProfile.userDetails[0],
          ...action.payload,
        };
      }
    },
    resetUserProfileData(state) {
      state.userProfile = {
        userDetails: [],
        hasAccess: false,
      };
    },
    setShowError(state, action) {
      state.showError = action.payload;
    },
    setShowToast(state, action) {
      state.showToast = action.payload.show;
      state.toastMessage = action.payload.toastMessage;
      state.type = action.payload.type;
      state.toastDuration = action.payload.toastDuration;
    },
    incrementRequests(state) {
      state.pendingRequests += 1;
      state.isLoading = true;
    },
    decrementRequests(state) {
      state.pendingRequests = Math.max(0, state.pendingRequests - 1);
      if (state.pendingRequests === 0) {
        state.isLoading = false;
      }
    },
    setRequires2FA(
      state,
      action: PayloadAction<{ requires2FA: boolean; userEmail?: string }>
    ) {
      state.requires2FA = action.payload.requires2FA;
      state.userEmail = action.payload.userEmail;
    },
  },
});

export const {
  setIsLoading,
  loginsuccess,
  setShowToast,
  setShowError,
  userProfileData,
  incrementRequests,
  decrementRequests,
  resetUserProfileData,
  updateProfileData,
  setRequires2FA,
} = slice.actions;

export default slice.reducer;

export function login({ login, password }: LoginCredentials) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = apiUrl.login;
      const response = await api.post(path, { login, password });
      // Handle 2FA response
      if (response.data?.is_two_factor_enabled) {
        dispatch(setRequires2FA({ requires2FA: true, userEmail: login }));
        dispatch(
          setShowToast({
            show: true,
            type: "info",
            toastMessage:
              "The verification code has been sent to your email. Please check and enter the code.",
            toastDuration: 3000,
          })
        );
        return { status: response.status, requires2FA: true };
      } else {
        dispatch(loginsuccess(true));
        dispatch(setRequires2FA({ requires2FA: false }));
        dispatch(
          setShowToast({
            show: true,
            type: "success",
            toastMessage: "Logged in Successfully",
          })
        );
        return { status: response.status, requires2FA: false };
      }
    } catch (error: any) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: error?.response?.data?.detail || "Login Failed",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function getUserProfile() {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = "/rfp/user/details/";
      const response = await api.get(path);
      const formattedRes = convertToCamelCase(response.data);
      dispatch(userProfileData(formattedRes));
      return response.status;
    } catch (error : any) {
      const errorMessage = error?.response?.data?.detail
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: errorMessage || "Something went wrong!",
          toastDuration: 3000,
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function userSignup(values: any) {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const body = {
        login: values.email,
        first_name: values.firstName,
        last_name: values.lastName,
        password: values.password,
        company_name: values.companyName,
        client_type: values.clientType,
        redirect_report: values.redirectReport,
      };
      const path = `open/user/registration/`;
      const { status, data } = await api.post(path, body);
      dispatch(
        setShowToast({
          show: true,
          type: "success",
          toastMessage: data?.message ?? "User registration successful!",
        })
      );
      return { status, data };
    } catch (error: any) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage:
            error?.response?.data?.detail ?? "Something went wrong!",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
}

export function verifyEmail(token: string) {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = `open/user/verify-email/?token=${token}`;
      const { status } = await api.get(path);
      return status;
    } catch (error: any) {
      const message = error?.response?.data?.detail ?? "Something went wrong!";
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: message,
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
}

export function sendResetPasswordLinkOnMail(mail: string) {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = `open/reset-password/send-email/`;
      const { status } = await api.post(path, { login: mail });
      return status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
}

export function resetUserPassword(values: { token: string; password: string }) {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = `open/reset-password/`;
      const response = await api.post(path, values);
      return response.status;
    } catch (error: any) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage:
            error?.response?.data?.detail ??
            "There were some issues while resetting your password, Please try again later.",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
}

export function verify2FA(code: string, login: string) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = "/open/login/verify-2fa/";
      const response = await api.post(path, {
        code,
        login,
      });

      if (response.data.status === 200) {
        dispatch(loginsuccess(true));
        dispatch(setRequires2FA({ requires2FA: false }));
        dispatch(
          setShowToast({
            show: true,
            type: "success",
            toastMessage: "Authentication successful. Logged in successfully!",
          })
        );
        return { status: response.status, success: true };
      }
    } catch (error: any) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage:
            error?.response?.data?.detail ||
            "Authentication failed. Please try again.",
        })
      );
      return { status: 400, success: false };
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function contactSupport(values: ContactForm) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `help/support/`;
      const response = await api.post(path, values);
      dispatch(
        setShowToast({
          show: true,
          type: "success",
          toastMessage: "Support ticket created successfully",
        })
      );
      return response.status;
    } catch (error: any) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage:
            error?.response?.data?.detail ??
            "There were some issues while contacting support, Please try again later.",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function contactSales(values: ContactSalesForm) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const body = {
        first_name: values.firstName,
        last_name: values.lastName,
        company: values.company,
        message: values.message,
        phone_number: values.phone,
      };

      const response = await api.post<typeof body>(apiUrl.contactSales, body);

      dispatch(
        setShowToast({
          show: true,
          type: "success",
          toastMessage: "Your request has been submitted successfully.",
        })
      );

      return response.status;
    } catch (error: any) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage:
            error?.response?.data?.detail ??
            "There were some issues while contacting sales, Please try again later.",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}
